import { useState } from 'react';
import {
  Typography,
  Card,
  Row,
  Col,
  Select,
  Divider,
  Statistic,
  Spin,
  Alert,
  Empty,
} from 'antd';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import { Container as MapContainer, NaverMap, Circle, InfoWindow, useNavermaps } from 'react-naver-maps';
import { useStatisticsOverview } from '@/hooks/useStatistics';

const { Title, Text } = Typography;

const TIER_COLORS: Record<string, string> = {
  BRONZE: '#CD7F32',
  SILVER: '#C0C0C0',
  GOLD: '#FFD700',
  PLATINUM: '#888',
};

export function StatisticsPage() {
  const [period, setPeriod] = useState<'7' | '30' | '90'>('30');
  const [openHeatIdx, setOpenHeatIdx] = useState<number | null>(null);
  const navermaps = useNavermaps();
  const days = parseInt(period, 10) as 7 | 30 | 90;
  const { data, isLoading, error } = useStatisticsOverview(days);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert
        message="통계 데이터 로드 실패"
        type="error"
        showIcon
      />
    );
  }

  const { summary, trends, scoreDistribution, regionHeatmap } = data;
  const scoreBuckets = scoreDistribution.buckets;
  const heatmapPoints = regionHeatmap.points;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            통계/분석
          </Title>
          <Text type="secondary">서비스 주요 지표 분석</Text>
        </div>
        <Select
          value={period}
          onChange={(v) => setPeriod(v)}
          style={{ width: 120 }}
          options={[
            { value: '7', label: '최근 7일' },
            { value: '30', label: '최근 30일' },
            { value: '90', label: '최근 90일' },
          ]}
        />
      </div>

      {/* 핵심 지표 요약 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center', borderRadius: 8 }}>
            <Statistic
              title="매칭 성사율"
              value={(summary.matchSuccessRate * 100).toFixed(1)}
              suffix="%"
              valueStyle={{ color: summary.matchSuccessRate >= 0.6 ? '#52c41a' : '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center', borderRadius: 8 }}>
            <Statistic
              title="평균 DAU"
              value={summary.avgDau.toLocaleString()}
              suffix="명"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center', borderRadius: 8 }}>
            <Statistic
              title="기간내 총 매칭"
              value={summary.totalMatches.toLocaleString()}
              suffix="건"
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ textAlign: 'center', borderRadius: 8 }}>
            <Statistic
              title="신규 가입"
              value={summary.totalNewUsers.toLocaleString()}
              suffix="명"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* DAU / 신규 가입 추이 */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card
            title={`DAU / 신규 가입 추이 (최근 ${period}일)`}
            style={{ borderRadius: 8 }}
          >
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  interval={period === '7' ? 0 : Math.floor(trends.length / 7)}
                />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="dau"
                  name="DAU"
                  stroke="#1890ff"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="newUsers"
                  name="신규 가입"
                  stroke="#52c41a"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* 일별 매칭 생성 */}
        <Col xs={24} lg={12}>
          <Card
            title={`일별 매칭 생성 (최근 ${period}일)`}
            style={{ borderRadius: 8 }}
          >
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  interval={period === '7' ? 0 : Math.floor(trends.length / 6)}
                />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip />
                <Bar dataKey="matches" name="매칭 수" fill="#fa8c16" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* 점수 분포 히스토그램 */}
        <Col xs={24} lg={12}>
          <Card
            title="점수 분포"
            style={{ borderRadius: 8 }}
            extra={
              <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
                {Object.entries(TIER_COLORS).map(([t, c]) => (
                  <span key={t} style={{ color: t === 'PLATINUM' ? '#888' : c }}>
                    ■ {t}
                  </span>
                ))}
              </div>
            }
          >
            {scoreBuckets.length === 0 ? (
              <Empty description="점수 데이터 없음" />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={scoreBuckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="rangeStart"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip
                    formatter={(v: number, _: string, props: { payload?: { rangeStart: number; rangeEnd: number } }) => [
                      v.toLocaleString() + '명',
                      `${props.payload?.rangeStart}~${props.payload?.rangeEnd}점`,
                    ]}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {scoreBuckets.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={TIER_COLORS[entry.tier] || '#CD7F32'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        {/* 지역별 활성도 히트맵 */}
        <Col xs={24}>
          <Card
            title={`지역별 매칭 요청 히트맵 (최근 ${period}일)`}
            style={{ borderRadius: 8 }}
          >
            {heatmapPoints.length === 0 ? (
              <Empty description="기간 내 지역 데이터 없음" style={{ padding: 80 }} />
            ) : (
              <MapContainer style={{ height: 400, width: '100%', borderRadius: 8, overflow: 'hidden' }}>
                <NaverMap
                  defaultCenter={new navermaps.LatLng(heatmapPoints[0].lat, heatmapPoints[0].lng)}
                  defaultZoom={11}
                >
                  {heatmapPoints.map((point, i) => (
                    <Circle
                      key={i}
                      center={new navermaps.LatLng(point.lat, point.lng)}
                      radius={Math.max(400, point.intensity * 30)}
                      fillColor="#1890ff"
                      fillOpacity={Math.min(0.5, point.intensity / 200)}
                      strokeColor="#1890ff"
                      strokeWeight={1}
                      clickable
                      onClick={() => setOpenHeatIdx(i)}
                    />
                  ))}
                  {openHeatIdx !== null && heatmapPoints[openHeatIdx] && (
                    <InfoWindow
                      position={
                        new navermaps.LatLng(
                          heatmapPoints[openHeatIdx].lat,
                          heatmapPoints[openHeatIdx].lng
                        )
                      }
                      content={`<div style="padding:8px 10px;font-size:13px;line-height:1.6;"><strong>${heatmapPoints[openHeatIdx].label}</strong><br/>활성도: ${heatmapPoints[openHeatIdx].intensity}</div>`}
                    />
                  )}
                </NaverMap>
              </MapContainer>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
